/**
 * MEVIA - Integrated Report Tab
 * Shows combined Dimensions + Enneagram reports with comparisons and HR indicators
 * Uses the same RadarChart component as the Culture Reports
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Building,
  Users,
  User,
  AlertTriangle,
  CheckCircle,
  Download,
  Loader2,
  Info,
  TrendingUp,
  Target,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import HRIndicatorsPanel from './HRIndicatorsPanel';

// Import the same RadarChart used in Culture Reports
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import logger from '../../utils/logger';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// Dimension names mapping - Correct translations
const DIMENSION_NAMES = {
  fisico: { es: 'Físico', en: 'Physical' },
  humano: { es: 'Humano', en: 'Human' },
  manufacturado: { es: 'Herramientas', en: 'Tools' },
  financiero: { es: 'Financiero', en: 'Financial' },
  natural: { es: 'Natural', en: 'Natural' },
  cultural: { es: 'Cultural', en: 'Cultural' },
  social: { es: 'Social', en: 'Social' },
  conocimiento: { es: 'Conocimiento', en: 'Knowledge' },
  psicologico: { es: 'Psicológico', en: 'Psychological' },
  espiritual: { es: 'Propósito', en: 'Purpose' },
  // Also handle capital_ prefixed keys
  capital_conocimiento: { es: 'Conocimiento', en: 'Knowledge' },
  capital_cultural: { es: 'Cultural', en: 'Cultural' },
  capital_espiritual: { es: 'Propósito', en: 'Purpose' },
  capital_financiero: { es: 'Financiero', en: 'Financial' },
  capital_fisico: { es: 'Físico', en: 'Physical' },
  capital_humano: { es: 'Humano', en: 'Human' },
  capital_manufacturado: { es: 'Herramientas', en: 'Tools' },
  capital_natural: { es: 'Natural', en: 'Natural' },
  capital_psicologico: { es: 'Psicológico', en: 'Psychological' },
  capital_social: { es: 'Social', en: 'Social' }
};

// Enneagram configurations with full descriptions
const ENNEAGRAM_TYPES = {
  1: { 
    name: { es: 'El Perfeccionista', en: 'The Perfectionist' }, 
    icon: '🎯', 
    color: '#6366F1',
    brief: { es: 'Ético, organizado, busca mejorar todo', en: 'Ethical, organized, seeks to improve everything' }
  },
  2: { 
    name: { es: 'El Ayudador', en: 'The Helper' }, 
    icon: '❤️', 
    color: '#EC4899',
    brief: { es: 'Generoso, empático, orientado a relaciones', en: 'Generous, empathetic, relationship-oriented' }
  },
  3: { 
    name: { es: 'El Triunfador', en: 'The Achiever' }, 
    icon: '🏆', 
    color: '#F59E0B',
    brief: { es: 'Ambicioso, eficiente, orientado al éxito', en: 'Ambitious, efficient, success-oriented' }
  },
  4: { 
    name: { es: 'El Individualista', en: 'The Individualist' }, 
    icon: '🎨', 
    color: '#8B5CF6',
    brief: { es: 'Creativo, sensible, busca autenticidad', en: 'Creative, sensitive, seeks authenticity' }
  },
  5: { 
    name: { es: 'El Investigador', en: 'The Investigator' }, 
    icon: '🔬', 
    color: '#06B6D4',
    brief: { es: 'Analítico, reservado, acumula conocimiento', en: 'Analytical, reserved, accumulates knowledge' }
  },
  6: { 
    name: { es: 'El Leal', en: 'The Loyalist' }, 
    icon: '🛡️', 
    color: '#78716C',
    brief: { es: 'Responsable, comprometido, busca seguridad', en: 'Responsible, committed, seeks security' }
  },
  7: { 
    name: { es: 'El Entusiasta', en: 'The Enthusiast' }, 
    icon: '🎉', 
    color: '#22C55E',
    brief: { es: 'Optimista, versátil, busca experiencias', en: 'Optimistic, versatile, seeks experiences' }
  },
  8: { 
    name: { es: 'El Desafiador', en: 'The Challenger' }, 
    icon: '⚡', 
    color: '#EF4444',
    brief: { es: 'Decidido, protector, busca control', en: 'Decisive, protective, seeks control' }
  },
  9: { 
    name: { es: 'El Pacificador', en: 'The Peacemaker' }, 
    icon: '☮️', 
    color: '#64748B',
    brief: { es: 'Tranquilo, mediador, busca armonía', en: 'Calm, mediator, seeks harmony' }
  }
};

// Comparison Radar Chart Component - Same style as Culture Reports
const ComparisonRadarChart = ({ dimensions, employee }) => {
  const { language } = useLanguage();
  if (!dimensions || dimensions.length === 0) {
    return (
      <div className="flex items-center justify-center h-[350px] bg-gray-50 rounded-lg">
        <p className="text-gray-500">
          {language === 'es' ? 'No hay datos disponibles' : 'No data available'}
        </p>
      </div>
    );
  }

  const labels = dimensions.map(d => {
    const key = d.key || d.dimension || '';
    return DIMENSION_NAMES[key]?.[language] || d.label || key;
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: employee?.nombre || (language === 'es' ? 'Empleado' : 'Employee'),
        data: dimensions.map(d => d.employee || 0),
        backgroundColor: 'rgba(26, 135, 141, 0.2)',
        borderColor: 'rgba(26, 135, 141, 1)',
        pointBackgroundColor: 'rgba(26, 135, 141, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(26, 135, 141, 1)',
        borderWidth: 2
      },
      {
        label: employee?.departamento || (language === 'es' ? 'Departamento' : 'Department'),
        data: dimensions.map(d => d.department || 0),
        backgroundColor: 'rgba(139, 92, 246, 0.2)',
        borderColor: 'rgba(139, 92, 246, 1)',
        pointBackgroundColor: 'rgba(139, 92, 246, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 2
      },
      {
        label: language === 'es' ? 'Empresa' : 'Company',
        data: dimensions.map(d => d.company || 0),
        backgroundColor: 'rgba(107, 114, 128, 0.15)',
        borderColor: 'rgba(107, 114, 128, 0.8)',
        pointBackgroundColor: 'rgba(107, 114, 128, 0.8)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(107, 114, 128, 0.8)',
        borderWidth: 2,
        borderDash: [5, 5]
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: { size: 11 },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.raw}%`;
          }
        }
      }
    },
    scales: {
      r: {
        beginAtZero: true,
        min: 0,
        max: 25,
        ticks: {
          stepSize: 5,
          font: { size: 9 },
          color: '#666',
          backdropColor: 'transparent'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1
        },
        angleLines: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1
        },
        pointLabels: {
          font: { size: 10, weight: 'bold' },
          color: '#333',
          padding: 10
        }
      }
    },
    elements: {
      line: {
        borderWidth: 2,
        tension: 0.1
      },
      point: {
        radius: 3,
        hoverRadius: 5,
        borderWidth: 2
      }
    }
  };

  return (
    <div style={{ height: '350px', width: '100%' }}>
      <Radar data={chartData} options={chartOptions} />
    </div>
  );
};

// Enhanced Correlation Component with proper data mapping
const CorrelationSection = ({ correlations }) => {
  const { language } = useLanguage();
  if (!correlations || correlations.length === 0) return null;
  
  const getCorrelationStyle = (confidence) => {
    const level = (confidence || '').toUpperCase();
    switch(level) {
      case 'HIGH': return { 
        bg: 'bg-green-100', 
        border: 'border-green-400', 
        text: 'text-green-700', 
        label: language === 'es' ? 'Alta' : 'High' 
      };
      case 'MEDIUM': return { 
        bg: 'bg-yellow-100', 
        border: 'border-yellow-400', 
        text: 'text-yellow-700', 
        label: language === 'es' ? 'Media' : 'Medium' 
      };
      case 'LOW': return { 
        bg: 'bg-red-100', 
        border: 'border-red-400', 
        text: 'text-red-700', 
        label: language === 'es' ? 'Baja' : 'Low' 
      };
      default: return { 
        bg: 'bg-gray-100', 
        border: 'border-gray-400', 
        text: 'text-gray-700', 
        label: '-' 
      };
    }
  };

  const getDimensionName = (dimension, lang) => {
    const key = dimension?.toLowerCase().replace(/ /g, '_');
    return DIMENSION_NAMES[key]?.[lang] || dimension?.replace('capital_', '').replace(/_/g, ' ') || '';
  };
  
  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="font-medium text-blue-800 mb-1">
          {language === 'es' ? '¿Qué significa esto?' : 'What does this mean?'}
        </p>
        <p className="text-blue-700 text-xs">
          {language === 'es' 
            ? 'Las correlaciones muestran qué tan naturalmente conecta tu tipo de personalidad con cada dimensión cultural. Una correlación alta significa que probablemente te sentirás cómodo y motivado en ambientes que enfatizan esa dimensión.'
            : 'Correlations show how naturally your personality type connects with each cultural dimension. A high correlation means you will likely feel comfortable and motivated in environments that emphasize that dimension.'}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {correlations.map((corr, idx) => {
          const style = getCorrelationStyle(corr.confidence);
          const dimensionName = getDimensionName(corr.dimension, language);
          return (
            <div 
              key={idx}
              className={`p-3 rounded-lg ${style.bg} border ${style.border}`}
            >
              <p className={`font-semibold text-sm ${style.text} mb-2`}>
                {dimensionName}
              </p>
              <Badge className={`${style.bg} ${style.text} border ${style.border}`}>
                {style.label}
              </Badge>
              {corr.rationale && (
                <p className="text-xs text-gray-600 mt-2">
                  {corr.rationale}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const IntegratedReportTab = () => {
  const { language } = useLanguage();
  const [viewLevel, setViewLevel] = useState('company');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPDF, setExportingPDF] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    loadEmployeesList();
  }, []);

  useEffect(() => {
    loadReport();
  }, [viewLevel, selectedDepartment, selectedEmployee, language]);

  const loadEmployeesList = async () => {
    try {
      const res = await axios.get('/enneagram/integrated/employees-list');
      if (res.data.success) {
        setEmployees(res.data.data.employees || []);
        setDepartments(res.data.data.departments || []);
      }
    } catch (error) {
      logger.error('Error loading employees:', error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      let endpoint = `/enneagram/integrated/company?language=${language}`;
      
      if (viewLevel === 'department' && selectedDepartment) {
        endpoint = `/enneagram/integrated/department/${encodeURIComponent(selectedDepartment)}?language=${language}`;
      } else if (viewLevel === 'employee' && selectedEmployee) {
        endpoint = `/enneagram/integrated/employee/${selectedEmployee}?language=${language}`;
      }
      
      const res = await axios.get(endpoint);
      if (res.data.success) {
        setReport(res.data.data);
      }
    } catch (error) {
      logger.error('Error loading report:', error);
      toast.error(language === 'es' ? 'Error al cargar reporte' : 'Error loading report');
    } finally {
      setLoading(false);
    }
  };

  const handleViewChange = (value) => {
    setViewLevel(value);
    if (value === 'company') {
      setSelectedDepartment(null);
      setSelectedEmployee(null);
    } else if (value === 'department') {
      setSelectedEmployee(null);
    }
  };

  // PDF Export function
  const exportToPDF = async () => {
    if (!reportRef.current) {
      toast.error(language === 'es' ? 'Error al generar PDF' : 'Error generating PDF');
      return;
    }
    
    const loadingToast = toast.loading(
      language === 'es' ? 'Generando PDF...' : 'Generating PDF...'
    );
    setExportingPDF(true);
    
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = pdfWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;
      
      let heightLeft = scaledHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, scaledHeight);
      heightLeft -= pdfHeight;
      
      while (heightLeft > 0) {
        position = heightLeft - scaledHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, scaledHeight);
        heightLeft -= pdfHeight;
      }
      
      const fileName = `Integrated_Report_${viewLevel === 'employee' ? report?.employee?.nombre?.replace(/\s+/g, '_') : 'Company'}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.dismiss(loadingToast);
      toast.success(language === 'es' ? 'PDF descargado exitosamente' : 'PDF downloaded successfully');
    } catch (error) {
      logger.error('Error generating PDF:', error);
      toast.dismiss(loadingToast);
      toast.error(language === 'es' ? 'Error al generar el PDF' : 'Error generating PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  // Get dimension label
  const getDimensionLabel = (dim) => {
    const key = dim.key || dim.dimension || '';
    return DIMENSION_NAMES[key]?.[language] || dim.label || key;
  };

  // Render loading state
  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mr-3" style={{ color: '#1A878D' }} />
          <span className="text-gray-500">
            {language === 'es' ? 'Cargando reporte integrado...' : 'Loading integrated report...'}
          </span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Export Button */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center text-lg">
                <span className="text-xl mr-2">📊</span>
                {language === 'es' ? 'Reporte Integral' : 'Integrated Report'}
              </CardTitle>
              <CardDescription>
                {language === 'es' 
                  ? 'Análisis combinado de Cultura (Dimensiones) + Personalidad (Eneagrama)' 
                  : 'Combined analysis of Culture (Dimensions) + Personality (Enneagram)'}
              </CardDescription>
            </div>
            <Button 
              onClick={exportToPDF} 
              disabled={exportingPDF}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Download className="h-4 w-4 mr-2" />
              {exportingPDF 
                ? (language === 'es' ? 'Exportando...' : 'Exporting...') 
                : (language === 'es' ? 'Exportar PDF' : 'Export PDF')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            {/* View Level Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {language === 'es' ? 'Ver por' : 'View by'}
              </label>
              <Select value={viewLevel} onValueChange={handleViewChange}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">
                    <span className="flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      {language === 'es' ? 'Empresa' : 'Company'}
                    </span>
                  </SelectItem>
                  <SelectItem value="department">
                    <span className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {language === 'es' ? 'Departamento' : 'Department'}
                    </span>
                  </SelectItem>
                  <SelectItem value="employee">
                    <span className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {language === 'es' ? 'Empleado' : 'Employee'}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Department Selector */}
            {viewLevel === 'department' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {language === 'es' ? 'Departamento' : 'Department'}
                </label>
                <Select value={selectedDepartment || ''} onValueChange={setSelectedDepartment}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={language === 'es' ? 'Seleccionar...' : 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Employee Selector */}
            {viewLevel === 'employee' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {language === 'es' ? 'Empleado' : 'Employee'}
                </label>
                <Select value={selectedEmployee || ''} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder={language === 'es' ? 'Seleccionar...' : 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        <span className="flex items-center gap-2">
                          {emp.nombre}
                          {emp.has_both && <CheckCircle className="h-3 w-3 text-green-500" />}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report Content - ref for PDF export */}
      <div ref={reportRef} className="space-y-6 bg-white">
        {/* Missing Assessments Warning */}
        {report && viewLevel === 'employee' && report.missing_assessments && (
          (report.missing_assessments.dimensions || report.missing_assessments.enneagram) && (
            <Card className="border-l-4 border-l-amber-500 bg-amber-50">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">
                      {language === 'es' ? 'Evaluaciones Pendientes' : 'Pending Assessments'}
                    </p>
                    <ul className="text-sm text-amber-700 mt-1">
                      {report.missing_assessments.dimensions && (
                        <li>• {language === 'es' ? 'Evaluación de Cultura (10 Dimensiones)' : 'Culture Assessment (10 Dimensions)'}</li>
                      )}
                      {report.missing_assessments.enneagram && (
                        <li>• {language === 'es' ? 'Evaluación de Personalidad (Eneagrama)' : 'Personality Assessment (Enneagram)'}</li>
                      )}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        )}

        {/* HR Indicators Section - Integrated Context (Company Level) - BEFORE Culture section */}
        <HRIndicatorsPanel
          context="integrated"
          indicatorsToShow={['irr', 'ico', 'iap', 'ipc', 'icf', 'ipe', 'ilo']}
          showAlerts={true}
          showEmployees={false}
          showCandidates={false}
          title={language === 'es' ? 'Indicadores Integrales de RH' : 'Integrated HR Indicators'}
          description={language === 'es' 
            ? 'Análisis completo de la empresa combinando Cultura + Personalidad'
            : 'Complete company analysis combining Culture + Personality'}
          filterUserId={viewLevel === 'employee' ? selectedEmployee : null}
          filterDepartment={viewLevel === 'department' ? selectedDepartment : null}
        />

        {/* DIMENSIONS SECTION */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <span className="text-xl mr-2">🎯</span>
              {language === 'es' ? 'Cultura (10 Dimensiones)' : 'Culture (10 Dimensions)'}
            </CardTitle>
            <CardDescription>
              {language === 'es' 
                ? 'Comparación de valores en cada capital organizacional'
                : 'Comparison of values in each organizational capital'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {viewLevel === 'employee' && report?.dimensions && Array.isArray(report.dimensions) && report.dimensions.length > 0 ? (
              <div className="space-y-6">
                {/* Two column layout: Radar + Table */}
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Radar Chart - using Chart.js like Culture Reports */}
                  <div className="flex justify-center items-center">
                    <ComparisonRadarChart 
                      dimensions={report.dimensions}
                      employee={report.employee}
                    />
                  </div>
                  
                  {/* Comparison Table */}
                  <div>
                    <h4 className="font-semibold mb-3 text-sm">
                      {language === 'es' ? 'Tabla Comparativa' : 'Comparison Table'}
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="text-left p-2 font-semibold text-xs">{language === 'es' ? 'Dimensión' : 'Dimension'}</th>
                            <th className="text-center p-2 font-semibold text-xs" style={{ color: '#1A878D' }}>{report.employee?.nombre?.split(' ')[0] || 'Emp.'}</th>
                            <th className="text-center p-2 font-semibold text-xs" style={{ color: '#8B5CF6' }}>{language === 'es' ? 'Depto.' : 'Dept.'}</th>
                            <th className="text-center p-2 font-semibold text-xs text-gray-600">{language === 'es' ? 'Empresa' : 'Company'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.dimensions.map((dim, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="p-2 text-xs font-medium">{getDimensionLabel(dim)}</td>
                              <td className="text-center p-2">
                                <span className="font-bold text-xs" style={{ color: '#1A878D' }}>{dim.employee ?? '-'}%</span>
                              </td>
                              <td className="text-center p-2">
                                <span className="font-medium text-xs text-purple-600">{dim.department ?? '-'}%</span>
                              </td>
                              <td className="text-center p-2">
                                <span className="text-xs text-gray-600">{dim.company ?? '-'}%</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Insights */}
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="border-l-4 border-l-green-500 bg-green-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-green-700 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        {language === 'es' ? 'Fortalezas en Cultura' : 'Culture Strengths'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1 text-xs text-green-800">
                        {report.dimensions
                          .filter(d => (d.employee || 0) >= (d.company || 0))
                          .slice(0, 3)
                          .map((d, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <CheckCircle className="h-3 w-3" />
                              {getDimensionLabel(d)}: {d.employee}%
                            </li>
                          ))}
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-l-amber-500 bg-amber-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-amber-700 flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        {language === 'es' ? 'Áreas de Desarrollo' : 'Growth Areas'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1 text-xs text-amber-800">
                        {report.dimensions
                          .filter(d => (d.employee || 0) < (d.company || 0))
                          .slice(0, 3)
                          .map((d, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <AlertTriangle className="h-3 w-3" />
                              {getDimensionLabel(d)}: {d.employee}%
                            </li>
                          ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ) : viewLevel === 'company' && report?.dimensions?.has_data ? (
              <div>
                <h4 className="font-medium mb-4">
                  {language === 'es' ? 'Promedios de Empresa' : 'Company Averages'}
                </h4>
                <div className="space-y-3">
                  {Object.entries(report.dimensions.company_averages || {}).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-3">
                      <span className="w-28 text-sm capitalize font-medium">
                        {DIMENSION_NAMES[key]?.[language] || key.replace('_', ' ')}
                      </span>
                      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full"
                          style={{ width: `${value}%`, backgroundColor: '#1A878D' }}
                        />
                      </div>
                      <span className="w-12 text-right text-sm font-medium">{value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {language === 'es' 
                  ? 'Selecciona un empleado para ver el análisis de dimensiones' 
                  : 'Select an employee to view dimensions analysis'}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ENNEAGRAM SECTION */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <span className="text-xl mr-2">🎭</span>
              {language === 'es' ? 'Personalidad (Eneagrama)' : 'Personality (Enneagram)'}
            </CardTitle>
            <CardDescription>
              {language === 'es' 
                ? 'Perfil de personalidad y características del tipo'
                : 'Personality profile and type characteristics'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {viewLevel === 'employee' && report?.enneagram?.employee ? (
              <div className="space-y-6">
                {/* Employee Profile Card */}
                <div 
                  className="p-5 rounded-lg border-l-4"
                  style={{ borderLeftColor: report.enneagram.employee.primary_color, backgroundColor: `${report.enneagram.employee.primary_color}10` }}
                >
                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-4xl">{report.enneagram.employee.primary_icon}</span>
                    <div>
                      <p className="font-bold text-lg">
                        {language === 'es' ? 'Tipo' : 'Type'} {report.enneagram.employee.primary_type}: {report.enneagram.employee.primary_name}
                      </p>
                      <p className="text-gray-600 text-sm">
                        {report.enneagram.employee.primary_percentage}% {language === 'es' ? 'de afinidad' : 'affinity'}
                      </p>
                      {report.enneagram.employee.wing_type && (
                        <p className="text-sm text-gray-500 mt-1">
                          <span className="font-medium">{language === 'es' ? 'Ala' : 'Wing'}:</span>{' '}
                          <span className="text-base mr-1">{report.enneagram.employee.wing_icon}</span>
                          {language === 'es' ? 'Tipo' : 'Type'} {report.enneagram.employee.wing_type} - {report.enneagram.employee.wing_name}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Type Brief */}
                  <p className="text-gray-700 text-sm">
                    {ENNEAGRAM_TYPES[report.enneagram.employee.primary_type]?.brief?.[language]}
                  </p>
                </div>
                
                {/* Company Distribution */}
                {report.enneagram.company?.type_distribution && (
                  <div>
                    <h4 className="font-semibold mb-3 text-sm">
                      {language === 'es' ? 'Distribución en la Empresa' : 'Company Distribution'}
                    </h4>
                    <div className="space-y-2">
                      {report.enneagram.company.type_distribution.filter(t => t.count > 0).map((type) => (
                        <div key={type.type} className="flex items-center gap-2">
                          <span className="text-base w-6">{type.icon}</span>
                          <span className="w-16 text-xs">{language === 'es' ? 'Tipo' : 'Type'} {type.type}</span>
                          <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full"
                              style={{ 
                                width: `${type.percentage}%`,
                                backgroundColor: type.color
                              }}
                            />
                          </div>
                          <span className="w-10 text-right text-xs font-medium">{type.percentage}%</span>
                          {type.type === report.enneagram.employee.primary_type && (
                            <Badge className="text-xs" style={{ backgroundColor: '#1A878D' }}>
                              {language === 'es' ? 'Tú' : 'You'}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : viewLevel === 'company' && report?.enneagram?.has_data ? (
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div 
                    className="p-4 rounded-lg text-center"
                    style={{ backgroundColor: `${report.enneagram.dominant_type?.color}15` }}
                  >
                    <span className="text-3xl">{report.enneagram.dominant_type?.icon}</span>
                    <p className="font-medium mt-2 text-sm">
                      {language === 'es' ? 'Tipo Dominante' : 'Dominant Type'}
                    </p>
                    <p className="text-xs" style={{ color: report.enneagram.dominant_type?.color }}>
                      {language === 'es' ? 'Tipo' : 'Type'} {report.enneagram.dominant_type?.type}: {report.enneagram.dominant_type?.name}
                    </p>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>{report.enneagram.total_completed} {language === 'es' ? 'evaluaciones completadas' : 'completed assessments'}</p>
                    <p>{report.enneagram.completion_rate}% {language === 'es' ? 'tasa de completado' : 'completion rate'}</p>
                  </div>
                </div>
                
                <h4 className="font-medium mb-3 text-sm">
                  {language === 'es' ? 'Distribución de Tipos' : 'Type Distribution'}
                </h4>
                <div className="grid grid-cols-3 md:grid-cols-9 gap-2">
                  {report.enneagram.type_distribution?.map((type) => (
                    <div 
                      key={type.type}
                      className="p-2 rounded-lg text-center"
                      style={{ backgroundColor: type.count > 0 ? `${type.color}20` : '#F3F4F6' }}
                    >
                      <span className="text-xl">{type.icon}</span>
                      <p className="text-xs font-medium mt-1">{type.count}</p>
                      <p className="text-xs text-gray-500">{type.percentage}%</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {language === 'es' 
                  ? 'Selecciona un empleado para ver el análisis de personalidad' 
                  : 'Select an employee to view personality analysis'}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CORRELATIONS SECTION */}
        {viewLevel === 'employee' && report?.correlations && report.correlations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <span className="text-xl mr-2">🔗</span>
                {language === 'es' ? 'Correlaciones Personalidad-Cultura' : 'Personality-Culture Correlations'}
              </CardTitle>
              <CardDescription>
                {language === 'es' 
                  ? 'Cómo tu tipo de personalidad se relaciona naturalmente con cada dimensión cultural' 
                  : 'How your personality type naturally relates to each cultural dimension'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CorrelationSection 
                correlations={report.correlations} 
              />
            </CardContent>
          </Card>
        )}

        {/* CONCLUSIONS AND RECOMMENDATIONS */}
        {viewLevel === 'employee' && report?.enneagram?.employee && report?.dimensions && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <span className="text-xl mr-2">💡</span>
                {language === 'es' ? 'Conclusiones y Recomendaciones' : 'Conclusions & Recommendations'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="font-semibold text-blue-800 mb-2 text-sm">
                  {language === 'es' ? 'Resumen del Perfil' : 'Profile Summary'}
                </h5>
                <p className="text-blue-700 text-sm">
                  {language === 'es' 
                    ? `${report.employee?.nombre || 'El empleado'} presenta un perfil de personalidad Tipo ${report.enneagram.employee.primary_type} (${report.enneagram.employee.primary_name}), lo que indica una tendencia natural hacia ${ENNEAGRAM_TYPES[report.enneagram.employee.primary_type]?.brief?.es || 'características distintivas'}. En términos de cultura organizacional, destaca en las dimensiones donde sus valores se alinean con los de la empresa.`
                    : `${report.employee?.nombre || 'The employee'} presents a Type ${report.enneagram.employee.primary_type} personality profile (${report.enneagram.employee.primary_name}), indicating a natural tendency towards ${ENNEAGRAM_TYPES[report.enneagram.employee.primary_type]?.brief?.en || 'distinctive characteristics'}. In terms of organizational culture, they excel in dimensions where their values align with the company's.`}
                </p>
              </div>

              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h5 className="font-semibold text-green-800 mb-2 text-sm">
                  {language === 'es' ? 'Recomendaciones de Desarrollo' : 'Development Recommendations'}
                </h5>
                <ul className="text-green-700 text-sm space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {language === 'es' 
                      ? 'Aprovechar las fortalezas naturales del tipo de personalidad en proyectos que requieran estas habilidades.'
                      : 'Leverage natural personality type strengths in projects requiring these skills.'}
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {language === 'es' 
                      ? 'Trabajar en las dimensiones culturales donde existe mayor brecha respecto al promedio de la empresa.'
                      : 'Work on cultural dimensions where there is a larger gap compared to company average.'}
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {language === 'es' 
                      ? 'Considerar el ala del eneagrama para un desarrollo más balanceado de la personalidad.'
                      : 'Consider the enneagram wing for more balanced personality development.'}
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        {/* TYPES GUIDE */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <Info className="h-5 w-5 mr-2" style={{ color: '#8B5CF6' }} />
              {language === 'es' ? 'Guía de los 9 Tipos de Eneagrama' : 'Guide to the 9 Enneagram Types'}
            </CardTitle>
            <CardDescription>
              {language === 'es' 
                ? 'Referencia rápida de todos los tipos de personalidad'
                : 'Quick reference of all personality types'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(ENNEAGRAM_TYPES).map(([num, type]) => {
                const isSelected = viewLevel === 'employee' && report?.enneagram?.employee?.primary_type === parseInt(num);
                return (
                  <div 
                    key={num} 
                    className={`p-2 rounded-lg border flex items-start gap-2 ${isSelected ? 'ring-2 ring-offset-1' : ''}`}
                    style={{ 
                      borderLeftWidth: '3px', 
                      borderLeftColor: type.color,
                      borderColor: isSelected ? type.color : '#E5E7EB',
                      backgroundColor: isSelected ? `${type.color}10` : 'white'
                    }}
                  >
                    <span className="text-xl">{type.icon}</span>
                    <div className="min-w-0">
                      <p className="font-semibold text-xs">{language === 'es' ? 'Tipo' : 'Type'} {num}</p>
                      <p className="text-xs" style={{ color: type.color }}>{type.name[language]}</p>
                      <p className="text-xs text-gray-500 line-clamp-1">{type.brief[language]}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IntegratedReportTab;
